import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Paper, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import CustomButton from '../buttons/CustomButton';
import ColorPalette from '../../colorpalette/ColorPalette';
import { closureDeadlineText, closureExpiredText, closureOriginText } from './companyClosure';

/**
 * Card de status do ciclo de encerramento aberto, em Backup e dados (só o dono).
 * - grace: como começou, data prevista e dias restantes;
 * - expired: carência terminou, eliminação pela equipe da Gabriela.
 * Botões: "Exportar dados" (rola até o card de exportação) e "Manter conta"
 * quando o backend permite (closure.can_revert); senão "Assinar para manter".
 */
function CompanyClosureStatusCard({ closure, reverting, revertError, onExport, onRevert }) {
    const navigate = useNavigate();
    const expired = closure.state === 'expired';
    const canRevert = !expired && closure.can_revert === true;

    return (
        <Paper
            variant="outlined"
            sx={{ p: 2, borderColor: '#FFCDD2', backgroundColor: '#FFF8F8' }}
        >
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Encerramento da conta
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: ColorPalette.error,
                        backgroundColor: ColorPalette.errorLight,
                    }}
                >
                    <EventBusyOutlinedIcon fontSize="small" />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: ColorPalette.textDark }}>
                        {expired ? 'Carência encerrada' : 'Conta em encerramento'}
                    </Typography>
                    {expired ? (
                        <Typography variant="body2" sx={{ color: ColorPalette.textDark }}>
                            {closureExpiredText(closure)}
                        </Typography>
                    ) : (
                        <>
                            <Typography variant="body2" sx={{ color: ColorPalette.textDark }}>
                                {closureOriginText(closure)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: ColorPalette.textDark }}>
                                {closureDeadlineText(closure)}
                            </Typography>
                        </>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {canRevert
                            ? 'Exporte os dados antes da data prevista. Se mudou de ideia, mantenha a conta.'
                            : 'Até a eliminação, ainda dá para exportar os dados. Para manter a conta, assine um plano.'}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2 }}>
                <CustomButton
                    label="Exportar dados"
                    style="primary"
                    variant="outlined"
                    icon={<FileDownloadOutlinedIcon fontSize="small" />}
                    onClick={onExport}
                />
                {canRevert ? (
                    <CustomButton
                        label="Manter conta"
                        style="primary"
                        variant="contained"
                        loading={reverting}
                        enabled={!reverting}
                        onClick={onRevert}
                    />
                ) : (
                    <CustomButton
                        label="Assinar para manter"
                        style="primary"
                        variant="contained"
                        onClick={() => navigate('/subscription/plans')}
                    />
                )}
            </Box>

            {revertError && <Alert severity="error" sx={{ mt: 2 }}>{revertError}</Alert>}
        </Paper>
    );
}

export default CompanyClosureStatusCard;
